function cleanInputObject<T extends object, U = T>(input: {
  raw: T;
  fieldNameMap?: Partial<{ [key in keyof T]: string }>;
  removeFields?: Array<keyof T>;
  preserveEmpty?: boolean;
}): U {
  const { raw, fieldNameMap, removeFields, preserveEmpty } = input;
  const safeNameMap = fieldNameMap ?? ({} as Partial<{ [key in keyof T]: keyof U }>);
  return (Object.keys(raw) as Array<keyof T>).reduce((acc, k) => {
    const rawValue = raw[k];
    if ((isEmpty(rawValue) && !preserveEmpty) || removeFields?.includes(k)) {
      return acc;
    }

    const value = isObject(rawValue) ? cleanInputObject({ raw: rawValue }) : rawValue;
    if (safeNameMap[k]) {
      return { ...acc, [safeNameMap[k] as string]: value };
    }

    return { ...acc, [k]: value };
  }, {} as U);
}

function isObject(value: unknown): value is object {
  return typeof value === 'object' && !(value instanceof Date);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isEmpty = (value: any): boolean => {
  if (value === '') return true;
  if (value === undefined) return true;
  return value === null;
};

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };
function cleanJson(input: Json): Json {
  if (isEmpty(input)) {
    return input;
  } else if (Array.isArray(input)) {
    return input.filter((value) => !isEmpty(value)).map((value) => cleanJson(value));
  } else if (typeof input === 'number' || typeof input === 'string' || typeof input === 'boolean') {
    return input;
  } else {
    return Object.entries(input as Record<string, Json>).reduce((memo: Record<string, Json>, [key, value]) => {
      if (!isEmpty(value)) {
        memo[key] = cleanJson(value);
      }

      return memo;
    }, {});
  }
}

export { cleanInputObject, cleanJson };
export type { Json };
